/**
 * Сигналы в терминале.
 *
 * Хранятся ВСЕ пришедшие, включая непроходящие порог. Порог применяется
 * при отборе для отрисовки, а не при приёме: иначе достаточно подвинуть
 * ползунок, чтобы безвозвратно потерять уже полученные сигналы и уйти
 * за ними в сеть заново.
 *
 * Фильтрует та же функция `passesThreshold` из контрактов, которой на
 * этапе 06 будет фильтровать Core. Одно решение — одна реализация.
 */

import {
  isBotEnabled,
  resolveThresholds,
  thresholdFor,
  type BelowThresholdMode,
  type Side,
  type Signal,
  type Timeframe,
  type UserSettings,
} from '@specter/types';

/**
 * Откуда сигнал: своё правило или чужой бот.
 *
 * Это первое, что человек хочет отделить. «Мои правила» и «алгоритмы,
 * которые я подключил» — разные вещи по степени доверия: за первое он
 * отвечает сам, второе принёс кто-то другой. Пока их можно было
 * фильтровать только по имени источника, разделения не существовало —
 * приходилось помнить, какой из идентификаторов чей.
 */
export type SignalSource = 'ALL' | 'rule' | 'bot';

export interface SignalFilters {
  readonly side: Side | 'ALL';
  readonly source: SignalSource;
  readonly botId: string | 'ALL';
  /** Показывать только сигналы текущего таймфрейма. */
  readonly currentTimeframeOnly: boolean;
}

export const DEFAULT_FILTERS: SignalFilters = {
  side: 'ALL',
  source: 'ALL',
  botId: 'ALL',
  currentTimeframeOnly: true,
};

/** Правило это или бот. Пусто в сигнале — бот, как было раньше. */
export function sourceOf(signal: Signal): 'rule' | 'bot' {
  return signal.sourceKind === 'rule' ? 'rule' : 'bot';
}

/**
 * Чем один источник отличается от другого — в глазах человека.
 *
 * У подписки ключ включает таймфрейм: «Дивергенции» на трёх минутах и на
 * часе — две разные записи, и это правильно, считаются они порознь. Но в
 * списке источников это давало семь строк «Выгодные зоны PRO» подряд и
 * четыре «Дивергенции», между которыми не выбрать: подписаны они
 * одинаково, а чем отличаются — не сказано.
 *
 * Человек выбирает источник по имени: «покажи только дивергенции». Для
 * него источник — это имя, а не ключ подписки. Таймфрейм у него уже есть
 * отдельной галочкой.
 */
export function sourceKeyOf(signal: Signal): string {
  return signal.meta?.rule?.ruleName ?? signal.botId;
}

export interface SignalSelection {
  readonly signals: readonly Signal[];
  /** Профиль пользователя: пороги с наследованием, включённые боты, режим. */
  readonly settings: UserSettings;
  readonly filters: SignalFilters;
  readonly timeframe: Timeframe;
}

/** Сигнал, отобранный к показу, вместе с решением о его заметности. */
export interface VisibleSignal {
  readonly signal: Signal;
  /** Не прошёл порог, но показывается приглушённым. */
  readonly dimmed: boolean;
}

export function selectVisible(selection: SignalSelection): VisibleSignal[] {
  const { signals, settings, filters, timeframe } = selection;
  const visible: VisibleSignal[] = [];

  for (const signal of signals) {
    if (filters.currentTimeframeOnly && signal.timeframe !== timeframe) continue;
    if (filters.side !== 'ALL' && signal.side !== filters.side) continue;
    if (filters.source !== 'ALL' && sourceOf(signal) !== filters.source) continue;
    if (filters.botId !== 'ALL' && sourceKeyOf(signal) !== filters.botId) continue;
    // Отозванный сигнал не должен оставаться на графике как действующий.
    if (signal.status === 'INVALIDATED') continue;
    /*
     * Выключенный бот исчезает с графика целиком, а не приглушается:
     * это не «слабый сигнал», а «я его не слушаю».
     *
     * На правила этот список не распространяется: он про ботов, а
     * правило человек создал сам — выключается оно своим флажком, а не
     * чужим фильтром.
     */
    if (signal.sourceKind !== 'rule' && !isBotEnabled(settings.bots, signal.botId)) continue;

    /*
     * Порог больше ничего не прячет.
     *
     * Он остался в профиле и по-прежнему приглушает слабый сигнал, но
     * убирать сигнал с глаз он не вправе: настройки, которая им
     * управляет, в интерфейсе больше нет. Порог сигнала задаётся там,
     * где он и осмыслен, — в самом индикаторе, а невидимый глобальный
     * порог поверх него означал бы, что человек выставил семьдесят в
     * своём правиле, а лента молчит, и понять почему неоткуда.
     *
     * Приглушение оставлено: оно ничего не отнимает, а разницу между
     * сильным и слабым показывает.
     */
    const thresholds = resolveThresholds(
      settings.thresholds,
      signal.botId,
      signal.symbol,
    );
    const passes = signal.score >= thresholdFor(thresholds, signal.side);

    visible.push({ signal, dimmed: !passes });
  }

  return visible.sort((a, b) => a.signal.barTime - b.signal.barTime);
}

/**
 * Слияние пришедшего сигнала в ленту.
 *
 * Сигнал с известным id заменяет прежний, а не добавляется вторым:
 * бот вправе переприслать его, изменив статус или Score.
 */
export function mergeSignal(current: readonly Signal[], incoming: Signal): Signal[] {
  const index = current.findIndex((s) => s.id === incoming.id);
  if (index === -1) {
    const next = [...current, incoming];
    next.sort((a, b) => a.barTime - b.barTime || a.createdAt - b.createdAt);
    return next;
  }
  const next = [...current];
  next[index] = incoming;
  return next;
}

export interface BotOption {
  readonly id: string;
  readonly label: string;
  /** Бот подключён сейчас. Иначе он остался только в истории сигналов. */
  readonly connected: boolean;
}

/**
 * Боты для фильтра ленты.
 *
 * Объединение двух источников, и оба нужны:
 *   - подключённые сейчас — иначе бот, ещё не выдавший ни одного
 *     сигнала, не появится в списке, хотя в настройках он есть;
 *   - встреченные в сигналах — иначе отключившийся бот исчезнет из
 *     фильтра вместе с возможностью посмотреть его историю.
 */
export function botOptions(
  signals: readonly Signal[],
  connected: readonly { id: string; name: string }[] = [],
): BotOption[] {
  const options = new Map<string, BotOption>();

  for (const bot of connected) {
    options.set(bot.id, { id: bot.id, label: bot.name, connected: true });
  }
  for (const signal of signals) {
    /*
     * Ключ — имя источника, а не ключ подписки.
     *
     * Подписок у одного индикатора столько, сколько таймфреймов человек
     * включил, и раньше каждая давала свою строку в списке. Строки были
     * неотличимы: «Выгодные зоны PRO» семь раз подряд.
     */
    const key = sourceKeyOf(signal);
    if (options.has(key)) continue;

    /*
     * Сигнал правила — не отключённый бот.
     *
     * Имя правила лежит в самом сигнале, и показывать вместо него
     * `rule-mtewjzr8 (отключён)` — значит спрятать от человека его же
     * правило за служебным идентификатором. Правило вообще могло быть
     * удалено: событие осталось, и слово «отключён» о нём ничего не
     * говорит.
     */
    const rule = signal.meta?.rule;
    if (rule) {
      options.set(key, { id: key, label: rule.ruleName, connected: true });
      continue;
    }

    // Имени нет — бот отключён, показываем идентификатор из сигнала.
    options.set(key, {
      id: key,
      label: signal.botId,
      connected: false,
    });
  }

  return [...options.values()].sort((a, b) => a.label.localeCompare(b.label));
}
